import { useApiQuery } from "@gladis/api-client";
import { HouseholdSwitcher } from "@gladis/domain-identity/ui";

import { AccountMenu } from "../../components/nav/account-menu.js";
import { LanguageToggle } from "../../components/nav/language-toggle.js";
import { ThemeToggle } from "../../components/nav/theme-toggle.js";
import type { CurrentUserDto } from "@gladis/shared";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useCallback, useState, type ReactNode } from "react";

import { MobileNav, MobileNavTrigger, SideNav } from "../../components/nav/side-nav.js";

/**
 * The application shell, as a second PATHLESS layout nested inside the guard.
 *
 * It contributes no URL segment either, so `_shell/index.tsx` is still `/` and `_shell/savings.tsx`
 * is still `/savings`. What it buys is the same property `_authenticated` has: whether a screen
 * gets the navigation is decided by WHERE ITS FILE LIVES, not by a conditional inside a layout that
 * someone has to remember to update.
 *
 * That distinction is load-bearing exactly once, and it is why this is a separate layout rather
 * than chrome added to `_authenticated`: ONBOARDING sits outside it. Someone with no household has
 * nowhere to navigate to — every section would 404 or redirect straight back — so showing them a
 * rail of six destinations would be a lie. It keeps the centred entry-screen treatment instead.
 */
export const Route = createFileRoute("/_authenticated/_shell")({
  component: AppShell,
});

function AppShell(): ReactNode {
  /*
   * Served from the same cache entry `_authenticated` already populated, so this costs no extra
   * request — TanStack Query dedupes on the key.
   */
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const navigate = useNavigate();

  /*
   * THE DRAWER'S STATE LIVES HERE because its two halves are in two places: the trigger is in this
   * bar and the sheet is a sibling of it. Anything lower would have to reach across.
   *
   * `useCallback` because `MobileNav` closes itself on a breakpoint change, and its effect depends on
   * the handler — a new function identity every render would tear that listener down and rebuild it
   * on every render of the shell.
   */
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    /*
     * ==========================================================================================
     * THE CHROME IS AN L AND THE CONTENT IS A PANEL INSET INSIDE IT
     * ==========================================================================================
     *
     * The rail down the left and the bar across the top paint NOTHING above `md` — no fill, no
     * border, no blur. They show `--bg-page` and the animated waves exactly as the viewport has
     * them, which is what makes the two read as one continuous surface rather than as two elements
     * that happen to have been given the same colour. Nothing has to be kept in sync because there
     * is no colour to keep in sync.
     *
     * `<main>` is then the ONE thing on screen with a surface of its own: `bg-surface`, a translucent
     * lighten over that same ground. Translucent rather than opaque for the reason the token gives —
     * an opaque panel over four fifths of the viewport deletes the background instead of lightening
     * it — so the waves still roll under the content and simply read paler there.
     *
     * `md:pl-56` RESERVES THE RAIL, it does not contain it.
     *
     * `SideNav` is `fixed` at both breakpoints, so it is out of flow and this padding is the only
     * thing keeping the content clear of it. The pair has to stay in step: 56 here matches `md:w-56`
     * there, and there is no layout mechanism to catch it if they drift — a rail that overlapped the
     * content would just look like a margin that was too small.
     *
     * `min-h-dvh` with the panel as a `flex-1` child, so a short page still fills the window: a
     * dashboard with two cards on it would otherwise leave the surface ending halfway down and the
     * chrome colour below it, which reads as the page having failed to load the rest.
     */
    <div className="flex min-h-dvh flex-col md:pl-56">
      <SideNav />
      {/* Below `md` only, and mounted always — a `<dialog>` cannot be conditionally rendered and then
          told to open. See `MobileNav`. */}
      <MobileNav open={menuOpen} onClose={closeMenu} />

      {/*
       * The top bar. Chrome, and the same nothing the rail paints.
       *
       * `h-16` MATCHES `SideNav`'s wordmark row, so the household name and the first nav row begin
       * on the same line. It is the only vertical relationship between the rail and the content and
       * it is a number written in two files; moving one means moving the other.
       *
       * `md:px-8` matches the content column's own gutter below, so the name sits directly above the
       * page title rather than above the panel's edge.
       */}
      {/*
       * IT STICKS ON A PHONE, AND THAT IS NOT POLISH — IT IS REACHABILITY.
       *
       * The navigation used to be a bar pinned to the BOTTOM edge, so it was reachable from anywhere
       * on a page of any length. Replacing it with a button in this bar quietly took that away: a
       * header in normal flow scrolls off, so halfway down the expenses table there was no way to
       * reach the navigation at all without scrolling back to the top first. The bar had no such
       * problem and nothing in the change announced that it had been lost.
       *
       * IT NEEDS A SURFACE HERE AND NOWHERE ELSE. Above `md` the chrome deliberately paints nothing —
       * the rail and this bar show the gradient exactly as the viewport has it, which is what makes
       * them read as one continuous ground. A sticky bar has content passing UNDER it, so on a phone
       * it takes the fill and the blur the old bottom bar carried, for the same reason it carried
       * them. `md:static` puts it back in flow above the breakpoint, where the rail is already
       * on-screen and there is nothing to pin.
       */}
      <header
        className={
          "sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-4 " +
          "border-b border-line bg-page/85 px-4 backdrop-blur " +
          "md:static md:border-0 md:bg-transparent md:px-8 md:backdrop-blur-none"
        }
      >
        {/*
         * Whose finances these are. Here in the shell rather than on each page, so it is true of
         * every screen — a member should never have to wonder, and the dashboard was the only page
         * that used to say.
         *
         * As of feature 005 it SWITCHES as well as displays. Constitution v1.1.0 amended Principle
         * VII to permit multiple households and a switcher — the prohibition 004 respected appeared
         * in neither authority the principle defers to, and AC-H9 had required the switcher all
         * along.
         *
         * Someone with one household still sees precisely what 004 rendered. `HouseholdSwitcher`
         * makes that call, not this file, because "is there anything to switch to" is a question
         * about the data rather than about the layout.
         *
         * Rendered only once loaded. A placeholder would flash a wrong name on every navigation,
         * and the name is context rather than content, so its brief absence costs nothing.
         */}
        {/*
         * THE MENU BUTTON AND THE HOUSEHOLD NAME ARE ONE GROUP, so the bar stays a two-part row
         * — subject on the left, account controls on the right — rather than becoming three parts
         * that `justify-between` would space out evenly. The button is `md:hidden`; above that the
         * group is the switcher alone, exactly as it was.
         */}
        <div className="flex min-w-0 items-center gap-2">
          <MobileNavTrigger
            open={menuOpen}
            onOpen={() => {
              setMenuOpen(true);
            }}
          />
          {me.data ? (
            <HouseholdSwitcher
              me={me.data}
              onCreateHousehold={() => navigate({ to: "/onboarding" })}
            />
          ) : null}
        </div>

        {/*
         * THE ACCOUNT GROUP, at the far right. Two controls, and neither is a destination — which is
         * exactly why they are here rather than in the rail, whose own comment calls itself a list of
         * places. Added 2026-08-14; `side-nav.tsx` records what left it and why.
         *
         * The theme picker sits OUTSIDE the menu deliberately. It is one press to cycle and its current
         * state is worth seeing at a glance; burying it behind an avatar would make a one-press control a
         * two-press one and hide the state it exists to show.
         *
         * The avatar menu is rendered only once `me` has loaded, like the switcher beside it — a
         * placeholder avatar would flash a stranger's initials on every navigation.
         */}
        <div className="flex items-center gap-1">
          <LanguageToggle />
          <ThemeToggle />
          {me.data ? <AccountMenu me={me.data} /> : null}
        </div>
      </header>

      {/*
        THE GUTTERS ARE MARGINS AND NOT PADDING, and that is what keeps the page exactly one viewport
        tall. `min-h-dvh` above is a min-height on the CONTENT box; bottom padding there would sit
        outside it and give every short page a 16px scroll. A margin on a `flex-1` item is taken out
        of the space it is being asked to fill instead.

        Right and bottom only. The left and top edges are already bounded by the rail and the bar, so
        a gap there would be a second gutter beside a surface that is not there.

        Rounded on all four corners even though two of them are flush against chrome: the corner then
        shows the ground behind it, which is the same inset treatment the two free edges get. Below
        `md` the panel is full-bleed against a phone's edges and only the top corners round.
      */}
      <main className="flex-1 rounded-t-2xl bg-surface md:mr-4 md:mb-4 md:rounded-2xl">
        {/*
          `max-w-[1400px]` is the container width the design was drawn at. Wider than the 34rem
          reading measure on purpose: this column holds cards and charts, not prose.

          `min-w-0` is kept although this is no longer a flex item: `Page.Body` is a grid, and a grid
          item with intrinsically wide content (a table, a chart) resolves its track to that content
          unless something says it may shrink.

          THE BOTTOM PADDING USED TO BE `pb-28`, clearing a fixed phone nav bar along the bottom edge.
          That bar was replaced by a drawer on 2026-08-21, so there is nothing out of flow down there
          any more and 112px of blank space below every phone page was 112px of nothing.
        */}
        <div className="mx-auto w-full max-w-[1400px] min-w-0 px-4 pt-8 pb-10 md:px-8 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
